// src/components/Blogs.js
import React, { useEffect, useState } from 'react';
import { Box, Container, Typography, IconButton, CircularProgress } from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArticleIcon from '@mui/icons-material/Article';
import logo from '../assets/logo.png';

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(12px); }
  to { opacity: 1; transform: translateY(0); }
`;

const PageBg = styled(Box)(() => ({
  minHeight: '100vh',
  background: 'linear-gradient(180deg, #fffcf5 0%, #ffffff 60%)',
  display: 'flex',
  flexDirection: 'column',
}));

const PostCard = styled(Box)(({ theme }) => ({
  background: '#fff',
  border: '1px solid #f0f0f0',
  borderRadius: 20,
  overflow: 'hidden',
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  animation: `${fadeIn} 0.4s ease-out`,
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: '0 20px 40px rgba(0, 0, 0, 0.08)',
    borderColor: 'rgba(255, 183, 27, 0.3)',
  },
}));

function formatDate(d) {
  if (!d) return '';
  return new Date(d).toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' });
}

export default function Blogs() {
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch('/api/blogs')
      .then(r => r.json())
      .then(data => { if (data?.posts) setPosts(data.posts); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return (
    <PageBg>
      {/* Top bar */}
      <Box sx={{ py: 3 }}>
        <Container maxWidth="lg" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, cursor: 'pointer' }} onClick={() => navigate('/')}>
            <Box component="img" src={logo} alt="Rewarding" sx={{ height: 36 }} />
            <Typography sx={{ fontWeight: 800, fontSize: '1.3rem', color: '#111', letterSpacing: '-0.02em' }}>
              Rewarding
            </Typography>
          </Box>
          <IconButton onClick={() => navigate('/')} sx={{ color: '#666' }} aria-label="Back to home">
            <ArrowBackIcon />
          </IconButton>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ pb: 10, flex: 1 }}>
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Box sx={{
            display: 'inline-flex', alignItems: 'center', gap: 1,
            background: '#fff8e7', border: '1px solid #ffe0a0',
            borderRadius: 8, px: 2, py: 0.5, mb: 3,
          }}>
            <ArticleIcon sx={{ fontSize: 16, color: '#ffb71b' }} />
            <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#b07800' }}>
              The Rewarding Blog
            </Typography>
          </Box>
          <Typography sx={{
            fontWeight: 900, fontSize: { xs: '2.4rem', md: '3.2rem' }, color: '#111',
            lineHeight: 1.15, mb: 2, letterSpacing: '-0.02em',
          }}>
            Stories & Insights
          </Typography>
          <Typography sx={{ color: '#777', fontSize: '1.1rem', lineHeight: 1.7, maxWidth: 600, mx: 'auto' }}>
            Tips, strategies, and stories to help you grow your business with smart loyalty programs.
          </Typography>
        </Box>

        {loading ? (
          <Box sx={{ textAlign: 'center', py: 10 }}>
            <CircularProgress size={32} sx={{ color: '#ffb71b' }} />
          </Box>
        ) : posts.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 8, color: '#999' }}>
            <ArticleIcon sx={{ fontSize: 64, color: '#e0e0e0', mb: 2 }} />
            <Typography variant="body1" sx={{ mb: 0.5 }}>No posts yet</Typography>
            <Typography variant="body2" sx={{ color: '#bbb' }}>Check back soon for new articles.</Typography>
          </Box>
        ) : (
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' },
            gap: 4,
          }}>
            {posts.map(post => (
              <PostCard key={post._id} onClick={() => navigate(`/blogs/${post.slug}`)}>
                {post.coverImage ? (
                  <Box component="img" src={post.coverImage} alt={post.title} sx={{ width: '100%', height: 220, objectFit: 'cover', display: 'block' }} />
                ) : (
                  <Box sx={{
                    height: 220,
                    background: 'linear-gradient(135deg, #fff8e7 0%, #ffe0a0 100%)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <ArticleIcon sx={{ fontSize: 48, color: '#ffb71b', opacity: 0.5 }} />
                  </Box>
                )}
                <Box sx={{ p: 3 }}>
                  <Typography sx={{ fontSize: '0.8rem', color: '#999', mb: 1, fontWeight: 600 }}>
                    {formatDate(post.publishedAt || post.createdAt)}
                    {post.author ? ` • ${post.author}` : ''}
                  </Typography>
                  <Typography sx={{ fontWeight: 700, fontSize: '1.25rem', color: '#111', mb: 1.5, lineHeight: 1.3 }}>
                    {post.title}
                  </Typography>
                  {post.excerpt && (
                    <Typography sx={{ color: '#666', fontSize: '0.95rem', lineHeight: 1.6 }}>
                      {post.excerpt}
                    </Typography>
                  )}
                </Box>
              </PostCard>
            ))}
          </Box>
        )}
      </Container>
    </PageBg>
  );
}
